import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { X, Plus, FileText, Code, ChevronDown, Search, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from '@tanstack/react-router';

interface Template {
  id: string;
  name: string;
  subject: string;
  contentType: 'html' | 'plaintext';
  labels?: string[];
  score?: number;
}

interface AssignTemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMailboxes: any[];
  onAssignTemplates: (templateIds: string[]) => void;
}

// Mock templates data - replace with actual API call
const mockTemplates: Template[] = [
  {
    id: '1',
    name: 'Domain reputation reset',
    subject: 'Domain reputation reset',
    contentType: 'plaintext',
    labels: ['Cold Email 2'],
    score: 100,
  },
  {
    id: '2',
    name: 'Inbox delivery audit',
    subject: 'Inbox delivery audit',
    contentType: 'html',
    labels: ['test_temp'],
    score: 99,
  },
  {
    id: '3',
    name: 'Performance forecasting',
    subject: 'Performance forecasting',
    contentType: 'plaintext',
    labels: ['Cold Email 2'],
    score: 97,
  },
  {
    id: '4',
    name: 'Market share protection',
    subject: 'Market share protection',
    contentType: 'html',
    labels: ['Cold Email 2'],
    score: 96,
  },
  {
    id: '5',
    name: 'Executive email dashboard',
    subject: 'Executive email dashboard',
    contentType: 'plaintext',
    labels: ['Cold Email 2'],
    score: 98,
  },
  {
    id: '6',
    name: 'Revenue recovery solution',
    subject: 'Revenue recovery solution',
    contentType: 'html',
    labels: ['Cold Email 2'],
    score: 87,
  },
];

export function AssignTemplatesDialog({
  open,
  onOpenChange,
  selectedMailboxes,
  onAssignTemplates,
}: AssignTemplatesDialogProps) {
  const [selectedTemplates, setSelectedTemplates] = useState<Template[]>([]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [removeFilter, setRemoveFilter] = useState<'all' | 'common'>('all');
  const navigate = useNavigate();

  // Mock templates currently used in selected mailboxes (for Remove Templates section)
  const usedTemplates: Template[] = [
    {
      id: '1',
      name: 'Domain reputation reset',
      subject: 'Domain reputation reset',
      contentType: 'plaintext',
      labels: ['Cold Email 2'],
      score: 100,
    },
    {
      id: '2',
      name: 'Inbox delivery audit',
      subject: 'Inbox delivery audit',
      contentType: 'html',
      labels: ['test_temp'],
      score: 99,
    },
    {
      id: '3',
      name: 'Executive email dashboard',
      subject: 'Executive email dashboard',
      contentType: 'plaintext',
      labels: ['Cold Email 2'],
      score: 98,
    },
  ];

  // Filter templates based on search query
  const filteredTemplates = mockTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.subject.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Filter used templates based on remove filter
  const filteredUsedTemplates = removeFilter === 'common' 
    ? usedTemplates.filter(template => template.labels?.includes('Cold Email 2'))
    : usedTemplates;

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplates(prev => {
      if (prev.find(t => t.id === template.id)) {
        return prev.filter(t => t.id !== template.id);
      } else {
        return [...prev, template];
      }
    });
  };

  const handleTemplateRemove = (templateId: string) => {
    setSelectedTemplates(selectedTemplates.filter(t => t.id !== templateId));
  };

  const handleApplyChanges = () => {
    onAssignTemplates(selectedTemplates.map(t => t.id));
    setSelectedTemplates([]);
    setSearchQuery('');
    onOpenChange(false);
  };

  // Clear search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setShowTemplateSelector(false);
    }
  }, [open]);

  const handleCreateTemplate = (contentType: 'html' | 'plaintext') => {
    // Close the dialog first
    onOpenChange(false);
    
    // Navigate to template editor with content type parameter
    navigate({
      to: '/templates/editor',
      search: {
        type: contentType
      }
    });
  };

  const getContentTypeIcon = (contentType: 'html' | 'plaintext') => {
    return contentType === 'html' ? <Code className="h-3 w-3" /> : <FileText className="h-3 w-3" />;
  };

  const getContentTypeColor = (contentType: 'html' | 'plaintext') => {
    return contentType === 'html' ? '#3b82f6' : '#10b981'; // blue-500 and emerald-500
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="pb-2">
          <DialogTitle>Assign Templates</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Select New Templates Section */}
          <div className="space-y-3">
            <label className="text-sm font-medium">Select New Templates</label>
            <div className="relative">
              <div
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[40px] flex items-center cursor-pointer"
                onClick={() => setShowTemplateSelector(!showTemplateSelector)}
              >
                <span className="text-muted-foreground">Select templates</span>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>

              {/* Template Selector Dropdown */}
              {showTemplateSelector && (
                <div className="absolute z-10 w-full mt-1 border border-border rounded-md bg-background shadow-lg max-h-48 overflow-y-auto">
                  {/* Search Bar */}
                  <div className="sticky top-0 bg-background border-b p-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-background"
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  {/* Template List with Checkboxes */}
                  <div className="p-2 space-y-1">
                    {filteredTemplates.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No templates found
                      </div>
                    ) : (
                      filteredTemplates.map((template) => (
                        <div
                          key={template.id}
                          onClick={() => handleTemplateSelect(template)}
                          className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer"
                        >
                          <div className="flex items-center justify-center w-4 h-4 border border-border rounded">
                            {selectedTemplates.some(t => t.id === template.id) && (
                              <Check className="w-3 h-3 text-primary" />
                            )}
                          </div>
                            <div className={cn(
                              "w-5 h-5 rounded flex items-center justify-center text-white text-xs",
                              template.contentType === 'html' ? 'bg-blue-500' : 'bg-emerald-500'
                            )}>
                              {getContentTypeIcon(template.contentType)}
                            </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{template.name}</p>
                            <p className="text-xs text-muted-foreground">{template.subject}</p>
                          </div>
                          {template.score && (
                            <div className="w-6 h-6 rounded-full bg-green-100 text-green-800 text-xs flex items-center justify-center font-medium">
                              {template.score}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Remove Templates Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Remove Templates</label>
              <Tabs
                value={removeFilter}
                onValueChange={(value) => setRemoveFilter(value as 'all' | 'common')}
              >
                <TabsList className="h-8">
                  <TabsTrigger value="all" className="text-xs">
                    All
                  </TabsTrigger>
                  <TabsTrigger value="common" className="text-xs">
                    Common
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            {filteredUsedTemplates.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground border border-dashed border-border rounded-md">
                No templates selected
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {filteredUsedTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-white shadow-sm"
                    style={{ backgroundColor: getContentTypeColor(template.contentType) }}
                  >
                    <div className="w-3 h-3 rounded flex items-center justify-center">
                      {getContentTypeIcon(template.contentType)}
                    </div>
                    <span className="truncate max-w-[120px]">{template.name}</span>
                    <button
                      onClick={() => handleTemplateRemove(template.id)}
                      className="ml-0.5 hover:bg-white/20 rounded-full p-0.5 transition-colors"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between gap-3 pt-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="whitespace-nowrap">
                  Create New Template
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleCreateTemplate('html')}>
                  <Code className="h-4 w-4 mr-2" />
                  HTML
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleCreateTemplate('plaintext')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Plain Text
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="whitespace-nowrap"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApplyChanges}
                disabled={selectedTemplates.length === 0}
                className="whitespace-nowrap"
              >
                Apply Changes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}